import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { formatDistanceToNow } from "date-fns";

interface Discussion {
  _id: string;
  _creationTime: number;
  authorName: string;
  authorType: string;
  message: string;
  action?: string;
}

interface VideoDiscussionThreadProps {
  discussions: Discussion[] | undefined;
  emptyMessage?: string;
}

const getActionBadge = (action?: string) => {
  if (!action) return null;

  const badges = {
    approved: <Badge className="bg-green-500 text-white">Approved</Badge>,
    rejected: <Badge className="bg-red-500 text-white">Rejected</Badge>,
    contested: <Badge className="bg-yellow-500 text-white">Contested</Badge>,
    requested_revision: (
      <Badge className="bg-orange-500 text-white">Needs Revision</Badge>
    ),
    comment: null,
  };

  return badges[action as keyof typeof badges] || null;
};

const getAuthorTypeBadge = (authorType: string) => {
  const badges = {
    ai: (
      <Badge
        variant="outline"
        className="bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400"
      >
        🤖 AI
      </Badge>
    ),
    team: (
      <Badge
        variant="outline"
        className="bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400"
      >
        👥 Team
      </Badge>
    ),
    brand: (
      <Badge
        variant="outline"
        className="bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400"
      >
        👤 Brand
      </Badge>
    ),
    creator: (
      <Badge
        variant="outline"
        className="bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
      >
        ✨ Creator
      </Badge>
    ),
  };

  return badges[authorType as keyof typeof badges] || null;
};

export const VideoDiscussionThread = ({
  discussions,
  emptyMessage = "No discussion yet.",
}: VideoDiscussionThreadProps) => {
  if (!discussions || discussions.length === 0) {
    return (
      <Card className="p-4 text-center text-muted-foreground">
        {emptyMessage}
      </Card>
    );
  }

  return (
    <>
      {discussions.map((discussion) => (
        <Card key={discussion._id} className="p-4">
          <div className="flex gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold">
              {discussion.authorName?.charAt(0).toUpperCase() || "?"}
            </div>

            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm">
                  {discussion.authorName}
                </span>
                {getAuthorTypeBadge(discussion.authorType)}
                {getActionBadge(discussion.action)}
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(discussion._creationTime, {
                    addSuffix: true,
                  })}
                </span>
              </div>

              <p className="text-sm text-primary whitespace-pre-wrap">
                {discussion.message}
              </p>
            </div>
          </div>
        </Card>
      ))}
    </>
  );
};
